import React from "react";
import { TFunction } from "i18next";
import {
  IconHeart,
  IconDroplet,
  IconMan,
  IconRulerMeasure,
  IconCirclePlus,
  IconCircleMinus,
} from "@tabler/icons-react";
import { DayFrequency, ReminderFrequency } from "@/types/reminders";
import { VitalsParameter } from "@/types/vitals";
import { DerivedMemberType } from "@/types/member";
import vitalsTrackerStyles from "@/pages/vitals-tracker/VitalsTracker.module.css";
import styles from "./VitalsReminder.module.css";

export const VITALS_PARAM_CONFIG: Record<
  VitalsParameter,
  {
    labelKey: string;
    iconClass: string;
    Icon: React.FC<{ size: number; color: string }>;
  }
> = {
  [VitalsParameter.BP]: {
    labelKey: "vitalsTracker.bloodPressure",
    iconClass: vitalsTrackerStyles.paramIconBp,
    Icon: ({ size, color }) => <IconHeart size={size} color={color} />,
  },
  [VitalsParameter.GLUCOSE]: {
    labelKey: "vitalsTracker.bloodSugar",
    iconClass: vitalsTrackerStyles.paramIconGlucose,
    Icon: ({ size, color }) => <IconDroplet size={size} color={color} />,
  },
  [VitalsParameter.WEIGHT]: {
    labelKey: "vitalsTracker.weight",
    iconClass: vitalsTrackerStyles.paramIconWeight,
    Icon: ({ size, color }) => <IconMan size={size} color={color} />,
  },
  [VitalsParameter.HEIGHT]: {
    labelKey: "vitalsTracker.heightVital",
    iconClass: vitalsTrackerStyles.paramIconHeight,
    Icon: ({ size, color }) => (
      <span className={vitalsTrackerStyles.rotated90}>
        <IconRulerMeasure size={size} color={color} />
      </span>
    ),
  },
};

// Adults track BP/blood sugar/weight; children don't get a blood-pressure
// reminder option (matches VitalsTracker.tsx's own ADULT_PARAMETERS/
// CHILD_PARAMETERS split for the vitals-tracking feature) but do get
// Height, which adults don't need a recurring reminder for.
const ADULT_VITALS_REMINDER_PARAMETERS: VitalsParameter[] = [
  VitalsParameter.BP,
  VitalsParameter.GLUCOSE,
  VitalsParameter.WEIGHT,
];

const CHILD_VITALS_REMINDER_PARAMETERS: VitalsParameter[] = [
  VitalsParameter.HEIGHT,
  VitalsParameter.WEIGHT,
  VitalsParameter.GLUCOSE,
];

export const getVitalsReminderParameters = (
  derivedMemberType?: DerivedMemberType
): VitalsParameter[] =>
  derivedMemberType === DerivedMemberType.C
    ? CHILD_VITALS_REMINDER_PARAMETERS
    : ADULT_VITALS_REMINDER_PARAMETERS;

// Bullet-separated variant of getMedicationFrequencyText (remindersHelper.tsx)
// to match the Vitals summary card's "Every day • 4 times a day" copy.
// Weight/Height's "simple tracking" mode is always once-a-day under the
// hood (see isSimpleTrackingParam in index.tsx), so the "• Once a day"
// suffix is redundant noise there — omitDayFrequency drops it, leaving
// just "Every 45 days".
export const getVitalsFrequencyText = (
  frequency: ReminderFrequency | null,
  frequencyValue: number | null,
  dayFrequency: DayFrequency | null,
  t: TFunction,
  omitDayFrequency: boolean = false
): string => {
  if (!frequency) return "";

  const dayFreqText =
    dayFrequency && !omitDayFrequency
      ? t(`reminders.dayFrequency.${dayFrequency}`)
      : "";

  let frequencyText = "";
  switch (frequency) {
    case ReminderFrequency.EVERY_DAY:
      frequencyText = t("reminders.frequency.EVERY_DAY");
      break;
    case ReminderFrequency.EVERY_OTHER_DAY:
      frequencyText = t("reminders.frequency.EVERY_OTHER_DAY");
      break;
    case ReminderFrequency.DAY_FREQUENCY:
      if (!frequencyValue) return "";
      frequencyText = t("reminders.frequency.DAY_FREQUENCY_DISPLAY", {
        count: frequencyValue,
      });
      break;
    default:
      return "";
  }

  return dayFreqText ? `${frequencyText} • ${dayFreqText}` : frequencyText;
};

export const paintDaysBetweenTrackingField = (
  value: number | null,
  setValue: (value: number) => void,
  t: TFunction
) => {
  const current = value ?? 1;
  // Typing a value directly is faster than nudging +/- one tap at a time
  // to get from 1 to, say, 45 — the buttons stay for quick nudges, but the
  // number itself is now an editable field rather than read-only text.
  const handleTyped = (raw: string) => {
    if (raw === "") return;
    const parsed = parseInt(raw, 10);
    if (Number.isFinite(parsed)) {
      setValue(Math.max(1, parsed));
    }
  };
  return (
    <div className={styles.stepperCard}>
      <span className={styles.stepperLabel}>
        {t("reminders.vitals.daysBetweenTrackingLabel")}
      </span>
      <div className={styles.stepperControls}>
        <button
          type="button"
          className={styles.stepperButton}
          onClick={() => setValue(current + 1)}
        >
          <IconCirclePlus size={24} />
        </button>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          className={styles.stepperValue}
          value={current}
          onChange={(e) => handleTyped(e.target.value.replace(/\D/g, ""))}
          onBlur={() => {
            if (!value || value < 1) setValue(1);
          }}
          aria-label={t("reminders.vitals.daysBetweenTrackingLabel")}
        />
        <button
          type="button"
          className={styles.stepperButton}
          disabled={current <= 1}
          onClick={() => setValue(Math.max(1, current - 1))}
        >
          <IconCircleMinus size={24} />
        </button>
      </div>
    </div>
  );
};
